import { useCallback, useEffect, useRef, useState } from 'react';

import { EditButton } from '../buttons';
import FormInput from '../../../../_shared/FormInput';
import Underline from './Underline';

import { createStyles, makeStyles } from '@mui/styles';

const useStyles = makeStyles(() =>
    createStyles({
        editIcon: {
            height: 'inherit',
            width: 'inherit'
        },
        infoRow: {
            fontWeight: 300,
            alignItems: 'center',
            color: '#5A6A81',
            display: 'flex',
            fontSize: '0.85rem',
            '& i': {
                marginRight: '8px'
            },
            '& strong': {
                fontWeight: 800
            }
        },
        editIconContainer: {
            cursor: 'pointer',
            height: 'auto',
            margin: '0 0 0 15px',
            width: '1.5rem'
        },
        editUnderline: {
            backgroundColor: '#E3E4FA',
            border: '3px solid #E3E4FA',
            borderRadius: '10px',
            height: '0'
        },
        inputContainer: {
            marginBottom: '40px'
        },
        inputFields: {
            background: 'transparent',
            border: '0',
            borderRadius: '6px',
            color: '#1f1f51',
            fontFamily: 'Manrope',
            fontSize: '1.75rem',
            fontWeight: 500,
            letterSpacing: '-0.21px',
            padding: '10px 0px 0px 0px',
            resize: 'none',
            width: '100%',
            '&:focus': {
                outline: 'none !important'
            },
            '&:placeholder-shown': {
                fontStyle: 'italic'
            }
        },
        optionalLabel: {
            color: '#7A4406',
            fontSize: '0.8rem',
            fontWeight: 100,
            letterSpacing: '0.15em',
            marginLeft: '8px',
            opacity: 0.7
        },
        requiredLabel: {
            color: '#A80A2D',
            fontSize: '0.8rem',
            fontWeight: 100,
            letterSpacing: '0.15em',
            marginLeft: '8px',
            opacity: 0.7
        },
        saveInputBtnContainer: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            paddingBottom: '15px',
            '& button': {
                cursor: 'pointer',
                fontFamily: 'Manrope',
                fontSize: '16px',
                fontWeight: '800'
            }
        },
        underline: {
            backgroundColor: '#E3E4FA',
            border: '6px solid #E3E4FA',
            borderRadius: '10px',
            height: '0',
            marginBottom: '20px'
        }
    })
);

type NameInputProps = {
    inputTitleClass: (value: string) => string;
    intakePrivateFirstName: string;
    intakePrivateFullName: string;
    intakePrivateLastName: string;
    intakePublicName: string;
    remoteCopy: {
        label: string;
        placeholder: string;
        required: boolean;
    };
    setIntakePrivateFirstName: (value: string) => void;
    setIntakePrivateFullName: (value: string) => void;
    setIntakePrivateLastName: (value: string) => void;
    setIntakePublicName: (value: string) => void;
};

const NameInput: React.FC<NameInputProps> = ({ remoteCopy, ...props }) => {
    const classes = useStyles();
    const containerRef = useRef<HTMLDivElement>(null);
    const firstNameRef = useRef<HTMLTextAreaElement>(null);
    const lastNameRef = useRef<HTMLTextAreaElement>(null);

    const {
        inputTitleClass,
        intakePrivateFirstName,
        intakePrivateFullName,
        intakePrivateLastName,
        intakePublicName,
        setIntakePrivateFirstName,
        setIntakePrivateFullName,
        setIntakePrivateLastName,
        setIntakePublicName
    } = props;

    const { label, required } = remoteCopy;

    const [firstName, setFirstName] = useState(intakePrivateFirstName);
    const [fullNameInput, setFullNameInput] = useState(true);
    const [isActive, setIsActive] = useState(false);
    const [lastName, setLastName] = useState(intakePrivateLastName);
    const [publicName, setPublicName] = useState(intakePublicName);
    const [savedInput, setSavedInput] = useState(
        intakePrivateFullName.length > 0
    );

    const combineName = (first: string, last: string) =>
        `${first} ${last}`.trim();

    const handleEditClick = () => {
        setFullNameInput(false);
        setSavedInput(false);
    };

    const handleFirstNameInputClick = () => {
        setFullNameInput(false);
        setIsActive(true);
        setSavedInput(false);
    };

    const handleFullNameFocus = () => {
        setFullNameInput(false);
        setIsActive(true);
        setSavedInput(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;

        if (e.target.id === 'first-name-input') {
            setFirstName(value);
        } else if (e.target.id === 'last-name-input') {
            setLastName(value);
        } else if (e.target.id === 'public-name-input') {
            setPublicName(value);
        }
    };

    const handleSaveClick = useCallback(() => {
        setIntakePublicName(publicName);
        if (firstName && lastName) {
            setSavedInput(true);
        }

        if (firstName !== intakePrivateFirstName) {
            setIntakePrivateFirstName(firstName);
        }

        if (lastName !== intakePrivateLastName) {
            setIntakePrivateLastName(lastName);
        }

        if (
            firstName &&
            lastName &&
            intakePrivateFullName !== combineName(firstName, lastName)
        ) {
            setIntakePrivateFullName(combineName(firstName, lastName));
        }
    }, [
        firstName,
        intakePrivateFirstName,
        intakePrivateFullName,
        intakePrivateLastName,
        lastName,
        publicName,
        setIntakePrivateFirstName,
        setIntakePrivateFullName,
        setIntakePrivateLastName,
        setIntakePublicName,
        setSavedInput
    ]);

    useEffect(() => {
        if (firstName === '' || lastName === '') {
            setIntakePrivateFullName('');
        }
    }, [firstName, lastName, setIntakePrivateFullName]);

    useEffect(() => {
        if (firstNameRef.current && isActive) {
            firstNameRef.current.focus();
        }
    }, [firstName, isActive]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                if (
                    firstName &&
                    lastName &&
                    (intakePrivateFirstName !== firstName ||
                        intakePrivateLastName !== lastName) &&
                    intakePrivateFullName !==
                        combineName(firstName, lastName) &&
                    !savedInput
                ) {
                    handleSaveClick();
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [
        firstName,
        handleSaveClick,
        intakePrivateFirstName,
        intakePrivateFullName,
        intakePrivateLastName,
        lastName,
        savedInput
    ]);

    useEffect(() => {
        if (intakePrivateFirstName && intakePrivateLastName) {
            setSavedInput(true);
        }
    }, [intakePrivateFirstName, intakePrivateLastName]);

    return (
        <div className={classes.inputContainer} ref={containerRef}>
            {savedInput && (
                <span className={inputTitleClass(intakePrivateFullName)}>
                    {label}
                    <span
                        className={
                            classes[
                                required ? 'requiredLabel' : 'optionalLabel'
                            ]
                        }
                    >
                        {`(${required ? 'REQUIRED' : 'OPTIONAL'})`}
                    </span>
                </span>
            )}
            {fullNameInput && !savedInput && (
                <>
                    <span className={inputTitleClass(intakePrivateFullName)}>
                        {label}
                        <span
                            className={
                                classes[
                                    required ? 'requiredLabel' : 'optionalLabel'
                                ]
                            }
                        >
                            {`(${required ? 'REQUIRED' : 'OPTIONAL'})`}
                        </span>
                    </span>
                    <FormInput
                        autoComplete="on"
                        className={classes.inputFields}
                        name={'full name'}
                        onChange={(e) => e.preventDefault()}
                        onClick={handleFirstNameInputClick}
                        onFocus={handleFullNameFocus}
                        placeholder={'First Last'}
                        value={intakePrivateFullName}
                        aria-label={`${label} - Enter full name`}
                    />
                </>
            )}
            {!fullNameInput && !savedInput && (
                <>
                    <span
                        className={inputTitleClass(
                            intakePrivateFirstName ? intakePrivateFirstName : ''
                        )}
                    >
                        First Name
                    </span>
                    <span
                        className={
                            classes[
                                required ? 'requiredLabel' : 'optionalLabel'
                            ]
                        }
                    >
                        {`(${required ? 'REQUIRED' : 'OPTIONAL'})`}
                    </span>
                    <FormInput
                        autoComplete="on"
                        className={classes.inputFields}
                        id={'first-name-input'}
                        name={'first-name'}
                        onChange={handleInputChange}
                        onClick={() => setSavedInput(false)}
                        placeholder={'First Name'}
                        ref={firstNameRef}
                        saveInput={handleSaveClick}
                        value={firstName ? firstName : ''}
                        aria-label="First name - Enter your first name"
                    />
                    <Underline disabled={firstName === ''} />
                    <span
                        className={inputTitleClass(
                            intakePrivateLastName ? intakePrivateLastName : ''
                        )}
                    >
                        Last Name
                    </span>
                    <span
                        className={
                            classes[
                                required ? 'requiredLabel' : 'optionalLabel'
                            ]
                        }
                    >
                        {`(${required ? 'REQUIRED' : 'OPTIONAL'})`}
                    </span>
                    <FormInput
                        autoComplete="on"
                        className={classes.inputFields}
                        id={'last-name-input'}
                        name={'last-name'}
                        onChange={handleInputChange}
                        onClick={() => setSavedInput(false)}
                        placeholder={'Last Name'}
                        ref={lastNameRef}
                        saveInput={handleSaveClick}
                        value={lastName ? lastName : ''}
                        aria-label="Last name - Enter your last name"
                    />
                    <Underline disabled={lastName === ''} />
                    <span
                        className={inputTitleClass(intakePublicName ? intakePublicName : ''
                        )}
                    >
                        Public Name
                    </span>
                    <span className={classes.optionalLabel}>
                        (OPTIONAL)
                    </span>
                    <FormInput
                        autoComplete="on"
                        className={classes.inputFields}
                        id={'public-name-input'}
                        name={'public-name'}
                        onChange={handleInputChange}
                        onClick={() => setSavedInput(false)}
                        placeholder={'Enter a nickname'}
                        saveInput={handleSaveClick}
                        value={publicName ? publicName : ''}
                        aria-label="Public name - Enter Nickname"
                    />
                    <Underline disabled={false} marginBottom="10px" />
                    <div className={classes.infoRow}>
                        <i className="fas fa-info-circle" aria-label="info" />
                        <span>
                            {firstName ? (
                                <>
                                    We’ll use <strong>{firstName}</strong> if you leave this blank
                                </>
                            ) : (
                                <>We’ll use your first name if you leave this blank</>
                            )}
                        </span>
                    </div>
                </>
            )}
            {savedInput && (
                <EditButton
                    handleEditClick={handleEditClick}
                    inputValState={intakePrivateFullName}
                    saveInputState={savedInput}
                    ariaLabel={`Edit ${label}`}
                />
            )}
            {!savedInput && fullNameInput && <Underline />}
        </div>
    );
};

export default NameInput;